import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, KstDataGridColumn, KstDataGridPager, KstDataGridRows } from '@/components/KstDataGrid';

import { api } from '@/services/api-class';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getSubmoduleOrKerktonRoleFromReportAuditLogEntry = _wg('getSubmoduleOrKerktonRoleFromReportAuditLogEntry');
const npsdAuditLogEntryTemplate = _wg('npsdAuditLogEntryTemplate');

interface NPSDAuditLogProps {
  entityId: number | null;
  entityType: string; // 'person', 'vehicle', 'business', 'clientgroup'
  title?: string;
}

export function NPSDAuditLog({
  entityId = null,
  entityType,
  title = 'Audit Log',
}: NPSDAuditLogProps) {
  const { theme } = useTheme();

  const [entries, setEntries] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const entityTypeIdField = useMemo((): string => {
    const fieldMap: Record<string, string> = {
      'person': 'personId',
      'vehicle': 'vehicleId',
      'business': 'businessId',
      'clientgroup': 'clientGroupId',
    };
    return fieldMap[entityType] || `${entityType}Id`;
  }, [entityType]);

  const isNewRecord = useMemo((): boolean => {
    return !entityId || entityId <= 0;
  }, [entityId]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    return (month < 10 ? '0' : '') + month + '/' +
      (day < 10 ? '0' : '') + day + '/' +
      year + ' ' +
      hours + ':' +
      (minutes < 10 ? '0' : '') + minutes + ':' +
      (seconds < 10 ? '0' : '') + seconds + ' ' + ampm;
  }, []);

  const getRole = useCallback((entry: any): string => {
    if (typeof (window as any).getSubmoduleOrKerktonRoleFromReportAuditLogEntry === 'function') {
      return getSubmoduleOrKerktonRoleFromReportAuditLogEntry(entry);
    }
    return entry.kerktonRole || entry.role || '';
  }, []);

  const formatEntry = useCallback((entry: string): string => {
    if (!entry) return '';
    if (typeof (window as any).npsdAuditLogEntryTemplate === 'function') {
      return npsdAuditLogEntryTemplate(entry);
    }
    return entry;
  }, []);

  const getActionClass = useCallback((action: string): string => {
    switch (action) {
      case 'CREATE': return 'audit-action-create';
      case 'UPDATE': return 'audit-action-update';
      case 'STATUS_CHANGE': return 'audit-action-status';
      case 'DELETE': return 'audit-action-delete';
      case 'Viewed': return 'audit-action-view';
      default: return '';
    }
  }, []);

  const getActionLabel = useCallback((action: string): string => {
    switch (action) {
      case 'CREATE': return 'Created';
      case 'UPDATE': return 'Updated';
      case 'STATUS_CHANGE': return 'Status Change';
      case 'DELETE': return 'Deleted';
      case 'Viewed': return 'Viewed';
      default: return action;
    }
  }, []);

  const gridColumns = useMemo((): KstDataGridColumn[] => {
    return [
      {
        field: 'formattedDateTime', name: 'Date/Time', size: '160px',
        template: (e: any) => formatDateTime(e.dateCreated),
      },
      { field: 'loggedByFirstName', name: 'First Name', size: '100px' },
      { field: 'loggedByLastName', name: 'Last Name', size: '100px' },
      {
        field: 'role', name: 'Role', size: '100px',
        template: (e: any) => getRole(e),
      },
      {
        field: 'action', name: 'Action', size: '120px',
        template: (e: any) => `<span class="audit-action-badge ${getActionClass(e.action)}">${getActionLabel(e.action)}</span>`,
      },
      {
        field: 'entry', name: 'Details',
        template: (e: any) => formatEntry(e.entry),
      },
    ];
  }, [formatDateTime, getRole, getActionClass, getActionLabel, formatEntry]);

  const gridRows = useMemo((): KstDataGridRows => {
    return {
      entries: entries === null ? null : entries.map((e: any) => ({
        ...e,
        formattedDateTime: formatDateTime(e.dateCreated),
      })),
    };
  }, [entries, formatDateTime]);

  const gridPager = useMemo((): KstDataGridPager => {
    return {
      curPage: currentPage,
      numPages: totalPages,
      records: totalRecords,
      pageSize: pageSize,
      isVisible: totalPages > 1,
    };
  }, [currentPage, totalPages, totalRecords, pageSize]);

  const loadAuditLog = useCallback((page: number) => {
    if (entityId === null || entityId === undefined) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const operation = {
      data: {
        page: page,
        take: pageSize,
        skip: (page - 1) * pageSize,
      },
    };

    api.getNPSDAuditLogEntries(
      entityId,
      entityType,
      entityTypeIdField,
      page,
      String(pageSize),
      JSON.stringify(operation),
      (result: any) => {
        setEntries(result.entries || []);
        setTotalRecords(result.records || 0);
        setTotalPages(Math.ceil((result.records || 0) / pageSize) || 1);
        setIsLoading(false);
        setIsLoaded(true);
      }
    );
  }, [entityId, entityType, entityTypeIdField, pageSize]);

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadAuditLog(page);
  }, [totalPages, loadAuditLog]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    loadAuditLog(1);
  }, [loadAuditLog]);

  // Watch entityId changes
  useEffect(() => {
    if (entityId !== null && entityId !== undefined) {
      setCurrentPage(1);
      loadAuditLog(1);
    } else {
      setEntries([]);
      setIsLoaded(false);
    }
  }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`npsd-audit-log-wrapper ${theme}`}>
      <div className="npsd-audit-log-header">
        <h1>{title}</h1>
        {!isNewRecord && (
          <button
            className="btn-refresh"
            onClick={refresh}
            disabled={isLoading}
            title="Refresh"
          >
            <i className={isLoading ? 'fas fa-spinner fa-spin' : 'fas fa-redo'}></i>
          </button>
        )}
      </div>
      {isNewRecord ? (
        <div className="npsd-new-record-state">
          <i className="fas fa-info-circle"></i>
          <span>Audit log entries will appear after this record is saved.</span>
        </div>
      ) : (
        <KstDataGrid
          columns={gridColumns}
          rows={gridRows}
          pager={gridPager}
          loading={isLoading}
          showcheckbox={false}
          showheader={false}
          mobiletitlefield="formattedDateTime"
          onGoToPage={goToPage}
        />
      )}
    </div>
  );
}

(window as any).NPSDAuditLogReact = NPSDAuditLog;
